/**
 * Third-Party Script Lazy Loader
 * Delays loading of non-critical third-party scripts until user interaction
 * This improves Time to Interactive and reduces main thread blocking
 */
(function () {
    'use strict';

    angular
        .module('app.common')
        .factory('thirdPartyLoader', thirdPartyLoader);

    thirdPartyLoader.$inject = ['$window', '$timeout'];

    function thirdPartyLoader($window, $timeout) {
        var loaded = {};
        var hasUserInteracted = false;
        var tidioVisible = false;

        /**
         * Load a third-party script dynamically
         * @param {string} src - Script URL
         * @param {string} id - Unique identifier for the script
         * @param {object} options - Additional script attributes
         */
        function loadScript(src, id, options) {
            if (loaded[id]) {
                console.log('Third-party script already loaded:', id);
                return;
            }

            loaded[id] = true;
            console.log('Loading third-party script:', id);

            var script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.defer = true;

            // Add any additional attributes
            if (options) {
                Object.keys(options).forEach(function (key) {
                    script.setAttribute(key, options[key]);
                });
            }

            // Error handling
            script.onerror = function () {
                console.error('Failed to load third-party script:', id, src);
                loaded[id] = false;
            };

            document.body.appendChild(script);
        }

        /**
         * Load AND show Tidio chat widget after scroll (LCP optimization)
         * CRITICAL: Tidio script is NOT loaded until scroll to prevent LCP manipulation
         */
        function showTidio() {
            if (tidioVisible) return;
            tidioVisible = true;

            // Only load on desktop and non-quake mode
            var isDesktop = !$window.deviceType || $window.deviceType !== 'mobile';
            var isQuakeMode = $window.quake || false;

            if (!isDesktop || isQuakeMode) {
                console.log('Tidio skipped (mobile or quake mode)');
                return;
            }

            console.log('Scroll detected - loading Tidio script now (LCP-safe)');

            // Load Tidio script ONLY after scroll
            loadScript(
                'https://code.tidio.co/j5alrjdajkdksb5lhqle6ssooanm9dgf.js',
                'tidio',
                { 'fetchpriority': 'low' }
            );

            // Poll for Tidio element and make it visible
            var checkInterval = setInterval(function () {
                var tidioButton = document.getElementById('tidio-chat');

                if (tidioButton) {
                    clearInterval(checkInterval);

                    // Add fade-in class to show Tidio
                    tidioButton.classList.add('tidio-fade-in');
                    console.log('Tidio chat widget loaded and visible');

                    // Also show any wrapper divs
                    var tidioWrapper = document.querySelector('#tidio-chat-code + div');
                    if (tidioWrapper) {
                        tidioWrapper.classList.add('tidio-fade-in');
                    }
                }
            }, 100); // Check every 100ms

            // Timeout: Stop checking after 5 seconds
            $timeout(function () {
                clearInterval(checkInterval);
            }, 5000);
        }

        /**
         * Load all third-party scripts (including Tidio, but hidden via CSS)
         */
        function loadAllScripts() {
            if (hasUserInteracted) return;
            hasUserInteracted = true;

            console.log('User interaction detected - loading third-party scripts');


            // Only load on desktop and non-quake mode
            var isDesktop = !$window.deviceType || $window.deviceType !== 'mobile';
            var isQuakeMode = $window.quake || false;

            if (isDesktop && !isQuakeMode) {
                // Beamer - Product updates and announcements
                loadScript(
                    'https://app.getbeamer.com/js/beamer-embed.js',
                    'beamer',
                    { 'fetchpriority': 'low' }
                );

                // Tidio - NOT loaded here! Only loads after scroll (see showTidio function)
                // This prevents Tidio from manipulating DOM and affecting LCP
            }

            // Wootric - User feedback (load on all devices)
            loadScript(
                'https://cdn.wootric.com/wootric-sdk.js',
                'wootric',
                { 'fetchpriority': 'low' }
            );

            // Google Ads (DoubleClick)
            loadScript(
                'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
                'google-ads',
                {
                    'fetchpriority': 'low',
                    'unsafe-inline': true
                }
            );
        }

        /**
         * Initialize lazy loading
         * Waits for user interaction or timeout before loading scripts
         * CRITICAL: Delayed to prevent interfering with LCP measurement
         */
        function initLazyLoad() {
            // User interaction events (including scroll - loads all scripts)
            var events = ['scroll', 'mousemove', 'touchstart', 'touchmove', 'keydown', 'click'];

            // CRITICAL LCP FIX: Minimum delay of 3 seconds
            // LCP is typically measured within 2.5s - we wait 3s to be safe
            var minDelayForLCP = 3000; // 3 seconds minimum
            var timeoutDuration = 5000; // 5 seconds fallback
            var loadTriggered = false;
            var interactionTime = null;

            // Event handler to load scripts
            function onUserInteraction() {
                if (loadTriggered) return;

                interactionTime = Date.now();

                // Remove all event listeners
                events.forEach(function (eventName) {
                    $window.removeEventListener(eventName, onUserInteraction);
                });

                // CRITICAL: Wait minimum 3 seconds from page load before loading scripts
                // This prevents third-party scripts from manipulating DOM during LCP measurement
                var timeFromPageLoad = Date.now() - (window.performance && window.performance.timing ? window.performance.timing.navigationStart : 0);
                var remainingDelay = Math.max(0, minDelayForLCP - timeFromPageLoad);

                console.log('User interaction detected - waiting ' + remainingDelay + 'ms before loading third-party scripts (LCP protection)');

                $timeout(function () {
                    loadTriggered = true;
                    loadAllScripts();
                }, remainingDelay);
            }

            // Attach event listeners (passive for better performance)
            events.forEach(function (eventName) {
                $window.addEventListener(eventName, onUserInteraction, {
                    once: true,  // Automatically remove after first call
                    passive: true  // Doesn't block scrolling
                });
            });

            // Fallback timeout - also respects minimum LCP delay
            $timeout(function () {
                if (!loadTriggered) {
                    console.log('Timeout reached - loading third-party scripts');
                    loadTriggered = true;
                    loadAllScripts();
                }
            }, timeoutDuration);

            // Separate scroll handler to SHOW Tidio (script already loaded, just hidden)
            initTidioVisibility();

            console.log('Third-party lazy loader initialized (LCP-safe: 3s minimum delay)');
        }

        /**
         * Initialize Tidio visibility controller
         * Tidio script loads with other third-party, but visibility controlled ONLY by scroll
         */
        function initTidioVisibility() {
            var scrollThreshold = 300; // pixels (ONLY trigger - no timeout fallback)

            function onScroll() {
                var scrollY = $window.scrollY || $window.pageYOffset;

                if (scrollY > scrollThreshold) {
                    $window.removeEventListener('scroll', onScroll);
                    showTidio();
                }
            }

            // Listen for scroll events only (no timeout fallback)
            $window.addEventListener('scroll', onScroll, { passive: true });

            console.log('Tidio visibility controller initialized (scroll-only, threshold: ' + scrollThreshold + 'px)');
        }

        // Public API
        return {
            init: initLazyLoad,
            loadScript: loadScript,
            showTidio: showTidio  // Expose for manual triggering if needed
        };
    }
})();
